import { useEffect, useMemo, useState } from 'react';

import { FunctionsOutlined } from '@mui/icons-material';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Dialog, DialogContent, DialogTitle, Stack, Tooltip, Typography } from '@mui/material';

import ConditionsBuilderDialog from 'domains/content/components/Bee/ConditionsBuilderDialog';
import Autocomplete, { AutocompleteOption } from 'domains/core/components/Autocomplete';
import Button from 'domains/core/components/Button';
import SearchField from 'domains/core/components/SearchField';
import TableOffsetPagination from 'domains/core/components/TableOffsetPagination';
import { DisplayConditionListItem } from 'hooks/mutations/useArchiveDisplayCondition';
import { useManageDisplayConditions } from 'hooks/queries/useDisplayConditions';
import useDynamicContentCategories from 'hooks/queries/useDynamicContentCategories';
import useDebounce from 'hooks/useDebounce';
import { SEARCH_DEBOUNCE_MS } from 'utils';

import ManageDisplayConditionsTable from './ManageDisplayConditionsTable';

const DEFAULT_ROWS_PER_PAGE = 10;
const ALL_CATEGORIES_VALUE = '';

export type Props = {
    isDialogVisible: boolean;
    setIsDialogVisible: (isDialogVisible: boolean) => void;
};

const ManageDisplayConditionsModal = ({ isDialogVisible, setIsDialogVisible }: Props) => {
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [category, setCategory] = useState(ALL_CATEGORIES_VALUE);
    const [isArchivedView, setIsArchivedView] = useState(false);
    const [isCreateEditDialogVisible, setIsCreateEditDialogVisible] = useState(false);
    const [conditionToEdit, setConditionToEdit] = useState<DisplayConditionListItem | undefined>(undefined);

    const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

    const rowsPerPage = DEFAULT_ROWS_PER_PAGE;
    const offset = (page - 1) * rowsPerPage;

    // Go back to page 1 whenever a filter changes — the current page may no longer exist.
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, category, isArchivedView]);

    const { data: categoriesData } = useDynamicContentCategories();
    const categoryOptions: AutocompleteOption<string>[] = useMemo(
        () => (categoriesData ?? []).map((categoryName) => ({ label: categoryName, value: categoryName })),
        [categoriesData]
    );
    const selectedCategoryOption = category ? { label: category, value: category } : null;

    const { data, isLoading, isRefetching, refetch } = useManageDisplayConditions(
        {
            limit: rowsPerPage,
            offset,
            search: debouncedSearch.trim() || undefined,
            category: category || undefined,
            archived: isArchivedView,
        },
        { enabled: isDialogVisible, keepPreviousData: true }
    );

    const conditions = data?.data ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

    const openCreateDialog = () => {
        setConditionToEdit(undefined);
        setIsCreateEditDialogVisible(true);
    };

    const openEditDialog = (condition: DisplayConditionListItem) => {
        setConditionToEdit(condition);
        setIsCreateEditDialogVisible(true);
    };

    // Passed to ConditionsBuilderDialog as setIsDialogVisible — also clears conditionToEdit on
    // close, since that dialog does not do this itself (it's a controlled prop from here).
    const setCreateEditDialogVisible = (visible: boolean) => {
        setIsCreateEditDialogVisible(visible);
        if (!visible) {
            setConditionToEdit(undefined);
        }
    };

    const previousPage = () => {
        if (page > 1) {
            setPage((p) => p - 1);
        }
    };

    const nextPage = () => {
        if (page < totalPages) {
            setPage((p) => p + 1);
        }
    };

    const handleToggleArchivedView = () => {
        setIsArchivedView((prev) => !prev);
    };

    return (
        <>
            <Dialog
                maxWidth="lg"
                fullWidth
                open={isDialogVisible}
                onClose={() => setIsDialogVisible(false)}
                data-testid="manage-display-conditions-modal"
            >
                <DialogTitle>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack alignItems="center" direction="row" spacing={1}>
                            <FunctionsOutlined sx={{ paddingTop: '4px' }} />
                            <Typography variant="h6">manage display conditions</Typography>
                        </Stack>
                        <Button
                            variant="contained"
                            onClick={openCreateDialog}
                            data-testid="manage-display-conditions-create-button"
                        >
                            Create
                        </Button>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ padding: '16px 32px' }}>
                    <Stack direction="row" spacing={0} alignItems="center" mb={2}>
                        <Autocomplete
                            type="string"
                            options={categoryOptions}
                            value={selectedCategoryOption}
                            getOptionLabel={(option: AutocompleteOption<string>) => option.label}
                            isOptionEqualToValue={(
                                option: AutocompleteOption<string>,
                                value: AutocompleteOption<string>
                            ) => option.value === value?.value}
                            onChange={(_, newValue: AutocompleteOption<string> | null) =>
                                setCategory(newValue?.value ?? ALL_CATEGORIES_VALUE)
                            }
                            label="Category"
                            placeholder="Category"
                            size="small"
                            sx={{ minWidth: 320, maxWidth: 360 }}
                            data-testid="manage-display-conditions-category-select"
                        />
                        <Tooltip title={isArchivedView ? 'Active conditions' : 'Archived conditions'}>
                            <span>
                                <Button
                                    onClick={handleToggleArchivedView}
                                    data-testid="manage-display-conditions-archive-toggle-button"
                                    sx={{ ml: 0, mr: 0.5 }}
                                >
                                    {isArchivedView ? <UnarchiveIcon /> : <ArchiveIcon />}
                                </Button>
                            </span>
                        </Tooltip>
                        <SearchField
                            placeholder="Search display conditions..."
                            value={searchTerm}
                            onChange={setSearchTerm}
                            dataTestId="manage-display-conditions-search-input"
                            sx={{ minWidth: 260, maxWidth: 300 }}
                        />
                    </Stack>
                    <ManageDisplayConditionsTable
                        conditions={conditions}
                        isLoading={isLoading}
                        isRefetching={isRefetching}
                        isArchivedView={isArchivedView}
                        onEditCondition={openEditDialog}
                        refetch={refetch}
                    />
                    <TableOffsetPagination
                        page={page}
                        totalPages={totalPages}
                        onPreviousPage={previousPage}
                        onNextPage={nextPage}
                        rowsPerPage={rowsPerPage}
                        testIdPrefix="manage-display-conditions-pagination"
                    />
                </DialogContent>
            </Dialog>
            <ConditionsBuilderDialog
                isDialogVisible={isCreateEditDialogVisible}
                setIsDialogVisible={setCreateEditDialogVisible}
                conditionToEdit={conditionToEdit}
            />
        </>
    );
};

export default ManageDisplayConditionsModal;
